import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core';

// eslint-disable-next-line import/no-extraneous-dependencies
import Pubnub from 'pubnub';

import { EMPTY, filter, fromEvent, Observable, ReplaySubject, Subject, Subscription, take } from 'rxjs';
import { Logger } from '@sondermind/datadog-logging';
import { SentryClient } from '@sondermind/sentry-client';
import { ConfigurationService, IMemberAppConfig } from '@sondermind/configuration';
import { HttpService } from '@sondermind/http';
import { SondermindApplicationService } from '@sondermind/data-access/sondermind-app-service';

import { FEATURE_FLAG_SERVICE, FeatureFlagKey, IFeatureFlagService } from '@sondermind/data-access/feature-flags';
import { AUTH_SERVICE, IAuthService } from '@sondermind/utilities/models-auth';
import { IPubnubEventConsumerMessage, PubnubEventConsumerMessageTypes } from './pubnub-lib.interface';

interface TokenResponse {
  token: string;
  channel: string;
  subscribeKey: string;
  publishKey: string;
  uuid: string;
  authTtlMinutes: number;
}

@Injectable({
  providedIn: 'root'
})
export class PubnubEventConsumerLib implements OnDestroy {
  pubnub!: Pubnub;
  listener!: Pubnub.ListenerParameters;
  private channel!: string;
  private readonly messageSubject = new Subject<IPubnubEventConsumerMessage>();
  private readonly pubnubReadySubject = new ReplaySubject<Pubnub>(1);
  private tokenRefreshTimer?: ReturnType<typeof setTimeout>;
  private configSubscription?: Subscription;
  private beforeUnloadSubscription?: Subscription;
  private static readonly MAX_REFRESH_RETRIES = 3;
  private static readonly RETRY_BASE_DELAY_MS = 60_000; // 1 minute; doubles on each attempt
  private static readonly REFRESH_JITTER_MS = 2 * 60 * 1000; // ±2 minutes

  readonly pubnub$ = this.pubnubReadySubject.asObservable();
  readonly messages$: Observable<IPubnubEventConsumerMessage> = this.messageSubject.asObservable();

  constructor(
    private logger: Logger,
    private configurationService: ConfigurationService<IMemberAppConfig>,
    private http: HttpService,
    private ngZone: NgZone,
    @Inject(AUTH_SERVICE)
    private authService: IAuthService,
    private appService: SondermindApplicationService,
    @Inject(FEATURE_FLAG_SERVICE)
    private featureFlagService: IFeatureFlagService,
  ) {
    this.featureFlagService.isEnabled$(FeatureFlagKey.USE_PUBNUB_SERVICE_MESSAGING)
      .pipe(filter(Boolean), take(1))
      .subscribe(() => {
        // Disconnect Pubnub if there is a page unload or logout
        this.beforeUnloadSubscription = fromEvent(window, 'beforeunload')
          .pipe(take(1))
          .subscribe(() => this.cleanup());
        this.authService.authDestroyed$.pipe(take(1)).subscribe(() => {
          this.cleanup();
        });

        this.configSubscription = this.fetchPubnubConfig$()
          .subscribe({
            next: (data: TokenResponse) => {
              this.pubnub = this.initializePubnub(data);
              this.pubnubReadySubject.next(this.pubnub);
              if (data.authTtlMinutes) {
                this.scheduleTokenRefresh(data.authTtlMinutes);
              }
            },
            error: (error) => {
              this.logger.addError('Error getting pubnub config', error);
            }
          });
      });
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  private scheduleTokenRefresh(ttlMinutes: number): void {
    clearTimeout(this.tokenRefreshTimer);
    const refreshMs = PubnubEventConsumerLib.getRefreshDelay(ttlMinutes);
    this.tokenRefreshTimer = setTimeout(() => this.refreshToken(), refreshMs);
  }

  // Schedules the refresh at 80% of TTL plus a random ±2 minute jitter.
  // Jitter prevents thousands of users who logged in at the same time from all
  // refreshing their tokens simultaneously, which would create a thundering herd
  // against the bootstrap endpoint.
  private static getRefreshDelay(ttlMinutes: number): number {
    const baseMs = ttlMinutes * 60 * 1000 * 0.8;
    const jitter = (Math.random() * 2 - 1) * PubnubEventConsumerLib.REFRESH_JITTER_MS;
    return Math.max(0, baseMs + jitter);
  }

  private fetchPubnubConfig$(): Observable<TokenResponse> {
    if (!this.appService.currentUser.platformIdentity) {
      return EMPTY;
    }
    return this.http.post<TokenResponse>(`${this.configurationService.env.smPubnubApiBase}/api/v1/pubnub/provider/token`, {
      platformUuid: this.appService.currentUser.platformIdentity.replace('ident:', '')
    })
      .pipe(this.http.handleHttpErrorStrict());
  }

  private refreshToken(attempt = 0): void {
    this.fetchPubnubConfig$().subscribe({
      next: (data: TokenResponse) => {
        this.pubnub.setToken(data.token);
        this.logger.log('PubNub auth token refreshed');
        if (data.authTtlMinutes) {
          this.scheduleTokenRefresh(data.authTtlMinutes);
        }
      },
      error: (error: { status?: number; }) => {
        this.handleRefreshError(error, attempt);
      }
    });
  }

  private handleRefreshError(error: { status?: number; }, attempt: number): void {
    const status = error?.status ?? 0;

    if (status === 401 || status === 403) {
      // The AuthInterceptor has already attempted an Auth0 token refresh and failed,
      // which means the user's login session is expired and a logout/redirect is in
      // progress. Any further retries would just produce more 401s, so stop here.
      this.logger.warn('PubNub token refresh stopped: login session expired', { httpStatus: status });
      return;
    }

    if (attempt < PubnubEventConsumerLib.MAX_REFRESH_RETRIES) {
      const delayMs = PubnubEventConsumerLib.RETRY_BASE_DELAY_MS * 2 ** attempt;
      this.logger.warn(`PubNub token refresh failed (attempt ${attempt + 1} of ${PubnubEventConsumerLib.MAX_REFRESH_RETRIES}), retrying in ${delayMs / 1000}s`, { httpStatus: status });
      clearTimeout(this.tokenRefreshTimer);
      this.tokenRefreshTimer = setTimeout(() => this.refreshToken(attempt + 1), delayMs);
      return;
    }

    this.logger.addError('PubNub token refresh failed after max retries', error);
    SentryClient.captureException('PubNub token refresh failed after max retries', { extra: { error } });
  }

  private statusListener(statusEvent: Pubnub.StatusEvent | Pubnub.PubnubStatus): void {
    const { category } = statusEvent;
    switch (category) {
      case 'PNUnexpectedDisconnectCategory':
      case 'PNTimeoutCategory':
        this.handleErrorStatus(statusEvent);
        this.pubnub.reconnect();
        break;
      case 'PNNetworkUpCategory':
        this.logger.log('PubNub PNNetworkUpCategory');
        this.pubnub.reconnect();
        break;
      case 'PNNetworkDownCategory':
        this.handleErrorStatus(statusEvent);
        break;
      case 'PNAccessDeniedCategory':
        // Token likely expired mid-session; trigger an immediate refresh rather
        // than waiting for the scheduled timer to fire.
        this.handleErrorStatus(statusEvent);
        clearTimeout(this.tokenRefreshTimer);
        this.refreshToken();
        break;
      default:
        if (this.isPubnubError(statusEvent)) {
          this.incomingError(statusEvent);
        }
        break;
    }
  }

  private messageListener(messageEvent: Pubnub.MessageEvent): void {
    const message = this.getMessagePayload(messageEvent);
    if (this.isValidMessage(message)) {
      this.messageSubject.next(message);
      return;
    }
    this.logger.warn('Received PubNub message with unexpected payload', { messageEvent });
  }

  /**
   * Returns a filtered view of the PubNub message stream.
   * NOTE: emissions occur **outside Angular's zone** (see `runOutsideAngular` in the listener
   * setup). Angular subscribers must wrap the subscription callback in `ngZone.run()` to trigger
   * change detection; React subscribers are unaffected.
   */
  messagesByTypes$(messageTypes: PubnubEventConsumerMessageTypes[] | undefined, values?: Record<string, unknown>): Observable<IPubnubEventConsumerMessage> {
    if (!messageTypes?.length) {
      return EMPTY;
    }
    return this.messages$.pipe(
      filter((message) => message.type !== undefined && messageTypes.includes(message.type)),
      filter((message) => !values || this.hasMatchingValues(message, values)),
    );
  }

  initializePubnub(data: TokenResponse): Pubnub {
    const config: Omit<Pubnub.PubnubConfig, 'uuid'> & { userId: string; } = {
      publishKey: data.publishKey,
      subscribeKey: data.subscribeKey,
      userId: `${data.uuid}`,
      // networking
      ssl: true,
      keepAlive: true,
      // no need to catch up on the front-end applications
      restore: false,
      // announce when the network is down or up
      autoNetworkDetection: true,
      // since we don't use the Presence API, don't send out leave requests
      suppressLeaveEvents: true
    };

    const pubnub = new Pubnub(config as Pubnub.PubnubConfig & { userId: string; });
    pubnub.setToken(data.token);
    if (!data.channel) {
      this.logger.addError('PubNub token response missing channel field', data);
      pubnub.unsubscribeAll();
      pubnub.stop();
      return pubnub;
    }
    this.channel = data.channel;
    // Wrap each listener callback with runOutsideAngular so that PubNub
    // events never trigger Angular change detection, regardless of which zone
    // the PubNub SDK's internal WebSocket handler runs in.
    //
    // Wrapping addListener() alone is insufficient: PubNub stores our
    // callbacks and calls them as plain function calls from within its own
    // WebSocket onmessage handler, which runs in Angular's zone because
    // Zone.js patches WebSocket at PubNub initialisation time (inside
    // Angular's zone). The callback body must explicitly declare its own zone.
    this.listener = {
      status: (statusEvent: Pubnub.StatusEvent | Pubnub.PubnubStatus) => this.ngZone.runOutsideAngular(() => this.statusListener(statusEvent)),
      message: (messageEvent: Pubnub.MessageEvent) => this.ngZone.runOutsideAngular(() => this.messageListener(messageEvent)),
    };
    pubnub.addListener(this.listener);
    pubnub.subscribe({ channels: [this.channel] });
    return pubnub;
  }

  private handleErrorStatus(statusEvent: Pubnub.StatusEvent | Pubnub.PubnubStatus): void {
    if (this.isPubnubError(statusEvent)) {
      this.incomingError(statusEvent);
      return;
    }
    this.logger.warn('Received PubNub status category without error payload', { statusEvent });
  }

  private isValidMessage(message: unknown): message is IPubnubEventConsumerMessage {
    return typeof message === 'object' && message !== null && 'type' in message;
  }

  private hasMatchingValues(message: IPubnubEventConsumerMessage, values: Record<string, unknown>): boolean {
    return Object.entries(values).every(([key, value]) => message.payload?.[key] === value);
  }

  private getMessagePayload(messageEvent: Pubnub.MessageEvent): IPubnubEventConsumerMessage | undefined {
    return messageEvent.message as IPubnubEventConsumerMessage | undefined;
  }

  // type guard, StatusEvent (normal status messages) vs. PubnubStatus (error return from async call)
  isPubnubError(status: Pubnub.StatusEvent | Pubnub.PubnubStatus): status is Pubnub.PubnubStatus {
    return (status as Pubnub.PubnubStatus).error != null;
  }

  // handle incoming errors
  // when an async call fails, we get the failure through the status callback
  incomingError(status: Pubnub.PubnubStatus): void {
    if (status.error) {
      const statusMessage = status.errorData?.message || 'Unknown error message';
      const errorMessage = `${status.operation}: ${statusMessage} (${status.category})`;

      this.logger.addError(errorMessage, status);
    } else {
      this.logger.warn('Received non-error PubNub status in error handler', { pubnubStatus: status });
    }
  }

  private cleanup(): void {
    clearTimeout(this.tokenRefreshTimer);
    this.configSubscription?.unsubscribe();
    this.beforeUnloadSubscription?.unsubscribe();
    if (this.listener) {
      this.pubnub?.removeListener(this.listener);
    }
    this.pubnub?.unsubscribeAll();
    this.pubnub?.stop();
    this.messageSubject.complete();
    this.pubnubReadySubject.complete();
    this.logger.debug('PubNub event consumer destroyed');
  }
}
